import { UrlParams } from 'types/urlParams';

/**
 * What kind of entity a preview route is showing. Route segments and `urlParams.type` disagree on
 * spelling and granularity (`engagements` vs `engagement`, `collabmap` vs `collaborative-map`,
 * `/updates/...` reporting `projects`), so consumers classify once here rather than each
 * hand-rolling their own string comparisons.
 */
export const PreviewEntity = {
  ENGAGEMENT: 'engagement',
  LIVE_ENGAGEMENT: 'liveEngagement',
  COLLAB_MAP: 'collabMap',
  PAGE: 'page',
  QUESTIONNAIRE: 'questionnaire',
} as const;

export type PreviewEntity = (typeof PreviewEntity)[keyof typeof PreviewEntity];

const ENGAGEMENT_SEGMENT = 'engagements';
const LIVE_SEGMENT = 'live';
const COLLAB_MAP_TYPE = 'collaborative-map';
const ENTITY_SEGMENT_INDEX = 2;
const QUESTIONNAIRE_SEGMENT = 'questionnaires';

/** Trailing route segments that distinguish the two preview flavours. */
export const PREVIEW_SEGMENT = 'preview';
export const EXTERNAL_PREVIEW_SEGMENT = 'externalPreview';

/**
 * Collab maps are identified by `urlParams.type` because their route segment (`collabmap`) differs
 * from the type they report, and both engagement flavours are identified by segment because they
 * share a type. The questionnaire preview route has no organization, so it leads with its own
 * segment. Anything else — content pages, and the org home page, which has no entity segment —
 * resolves to PAGE.
 */
export const resolvePreviewEntity = (slug?: string[], urlParams?: UrlParams): PreviewEntity => {
  if (urlParams?.type === COLLAB_MAP_TYPE) {
    return PreviewEntity.COLLAB_MAP;
  }
  if (slug?.[0] === QUESTIONNAIRE_SEGMENT) {
    return PreviewEntity.QUESTIONNAIRE;
  }
  const segment = slug?.[ENTITY_SEGMENT_INDEX];
  if (segment === LIVE_SEGMENT) {
    return PreviewEntity.LIVE_ENGAGEMENT;
  }
  if (segment === ENGAGEMENT_SEGMENT) {
    return PreviewEntity.ENGAGEMENT;
  }
  return PreviewEntity.PAGE;
};

/**
 * Whether the entity has a tokenized `/externalPreview` route that a share link can point at.
 * Live engagements and the questionnaire preview have `/preview` only, so a share link for them
 * would 404 (ENG-4364).
 */
const EXTERNAL_PREVIEW_ENTITIES: readonly PreviewEntity[] = [
  PreviewEntity.ENGAGEMENT,
  PreviewEntity.COLLAB_MAP,
  PreviewEntity.PAGE,
];

export const supportsExternalPreview = (entity: PreviewEntity): boolean => EXTERNAL_PREVIEW_ENTITIES.includes(entity);

/**
 * Whether the entity's published version can be shared before it is public. Only pages have a
 * release date, and the gateway serves published share links for pages alone.
 */
const PUBLISHED_SHARE_ENTITIES: readonly PreviewEntity[] = [PreviewEntity.PAGE];

export const supportsPublishedShare = (entity: PreviewEntity): boolean => PUBLISHED_SHARE_ENTITIES.includes(entity);

/**
 * True only on the Auth0-gated `/preview` route, false on the public `/externalPreview` page that
 * a share link leads to. The recipient of a shared link must not be offered the share button, and
 * their path carries no org segment on a custom host, so no share URL can be built from it.
 */
export const isAuthenticatedPreviewRoute = (slug?: string[]): boolean =>
  !!slug?.length && slug[slug.length - 1] === PREVIEW_SEGMENT;

/** True on the public `/externalPreview` page that a share link leads to. */
export const isExternalPreviewRoute = (slug?: string[]): boolean =>
  !!slug?.length && slug[slug.length - 1] === EXTERNAL_PREVIEW_SEGMENT;

export type PreviewLinkTarget = 'activity' | 'page';

export interface PreviewLinkStyle {
  /** Keep the slugged default-host form with the `/preview` suffix: the live build, behind login. */
  stayInPreview: boolean;
  /** Link the public URL with `?previewLink=true`: the published version, collecting nothing. */
  addPreviewLink: boolean;
}

/**
 * How a link on a previewed page reaches its target. Only an admin on the authenticated `/preview`
 * stays in the live build, and only for pages. Activities always open their published version from
 * any preview, since reviewers want what residents get and a recipient could not open a draft. A
 * published or shared preview links pages to their published version too: behind login on the
 * authenticated route, which serves a page scheduled for release, and publicly everywhere else.
 */
export const resolvePreviewLinkStyle = (
  target: PreviewLinkTarget,
  urlParams?: UrlParams,
  slug?: string[],
): PreviewLinkStyle => {
  if (!urlParams?.preview) {
    return { stayInPreview: false, addPreviewLink: false };
  }
  if (target === 'page' && !urlParams.code) {
    if (!urlParams.previewLink) {
      return { stayInPreview: true, addPreviewLink: false };
    }
    if (isAuthenticatedPreviewRoute(slug)) {
      return { stayInPreview: true, addPreviewLink: true };
    }
  }
  return { stayInPreview: false, addPreviewLink: true };
};

/** Both engagement flavours collect resident responses, which pages and collab maps do not. */
export const isEngagementLike = (entity: PreviewEntity): boolean =>
  entity === PreviewEntity.ENGAGEMENT || entity === PreviewEntity.LIVE_ENGAGEMENT;

export const isCollabMapEntity = (entity: PreviewEntity): boolean => entity === PreviewEntity.COLLAB_MAP;
